import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Avatar, Box, Button, Card, CardActionArea, Chip, CircularProgress, Stack, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  Notifications as BellIcon, DoneAll as ReadAllIcon, CheckCircle as ReadIcon,
  Assignment as ReportIcon, PersonAdd as AssignedIcon, EventAvailable as QueueIcon,
  PlayCircleOutline as TurnIcon, Description as NoteIcon, EventNote as ApptIcon,
  TimerOutlined as ExpiringIcon, DeleteOutline as ExpiredIcon, MailOutline as MailIcon,
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationsContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TYPE_META = {
  report_added: { icon: <ReportIcon />, label: 'Report' },
  patient_assigned: { icon: <AssignedIcon />, label: 'Assigned' },
  queue_added: { icon: <QueueIcon />, label: 'Queue' },
  your_turn: { icon: <TurnIcon />, label: 'Your turn' },
  note_ready: { icon: <NoteIcon />, label: 'Note' },
  appointment_requested: { icon: <ApptIcon />, label: 'Request' },
  protocol_question_assigned: { icon: <NoteIcon />, label: 'Protocol' },
  recording_expiring: { icon: <ExpiringIcon />, label: 'Expiring' },
  recording_expired: { icon: <ExpiredIcon />, label: 'Deleted' },
  delivery_failed: { icon: <MailIcon />, label: 'Delivery' },
};
const LEGACY_ROUTES = {
  queue_added: '/queue', patient_assigned: '/queue', your_turn: '/queue',
};

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/**
 * Every notification, newest first. Unread ones are in colour with a highlighted edge;
 * read ones are greyed out. Opening one marks it read and goes where it points.
 */
export default function Notifications() {
  const navigate = useNavigate();
  const { markRead, markAllRead, refresh } = useNotifications();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState('all');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/clinical/notifications?limit=200`);
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (n) => {
    if (!n.is_read) {
      await markRead(n.id);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    const to = n.link || LEGACY_ROUTES[n.type];
    if (to) navigate(to);
  };

  const readAll = async () => {
    await markAllRead();
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
    refresh();
  };

  const shown = show === 'unread' ? items.filter((n) => !n.is_read) : items;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><BellIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            {unread ? `${unread} new` : 'You’re all caught up'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          <ToggleButtonGroup size="small" exclusive value={show} onChange={(e, v) => v && setShow(v)}>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="unread">New</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" size="small" startIcon={<ReadAllIcon />} onClick={readAll} disabled={!unread}>
            Mark all read
          </Button>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !shown.length ? (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          {show === 'unread' ? 'No new notifications.' : 'No notifications yet.'}
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {shown.map((n) => {
            const meta = TYPE_META[n.type] || {
              icon: <BellIcon />, label: String(n.type || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
            };
            const isNew = !n.is_read;
            return (
              <Card key={n.id} sx={{
                borderLeft: '4px solid', borderLeftColor: isNew ? 'primary.main' : 'transparent',
                bgcolor: isNew ? 'rgba(13,148,136,0.06)' : 'rgba(0,0,0,0.02)',
                opacity: isNew ? 1 : 0.62, filter: isNew ? 'none' : 'grayscale(1)',
              }}>
                <CardActionArea onClick={() => open(n)} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, justifyContent: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: isNew ? 'primary.main' : 'grey.400', width: 40, height: 40 }}>{meta.icon}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={isNew ? 800 : 500}>{n.title}</Typography>
                      <Chip size="small" variant="outlined" label={meta.label} sx={{ height: 20, fontSize: 11 }} />
                      {isNew && <Chip size="small" color="primary" label="New" sx={{ height: 20, fontSize: 11 }} />}
                    </Stack>
                    {n.message && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{n.message}</Typography>}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{when(n.created_at)}</Typography>
                  </Box>
                  {!isNew && <ReadIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.5 }} />}
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
